import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";

export const DateRangeFilter: React.FC<ColumnFilterProps> = ({label, onChange, filterParams}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 350);

    const getInitialStartDate = useCallback((): string => {
        if (filterParams.startDate && typeof filterParams.startDate === "string") {
            return filterParams.startDate;
        }
        return "";
    }, [filterParams.startDate]);

    const getInitialEndDate = useCallback((): string => {
        if (filterParams.endDate && typeof filterParams.endDate === "string") {
            return filterParams.endDate;
        }
        return "";
    }, [filterParams.endDate]);

    const [startDate, setStartDate] = useState<string>(getInitialStartDate);
    const [endDate, setEndDate] = useState<string>(getInitialEndDate);

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    }, [isOpen]);

    const handleApply = useCallback(() => {
        if (startDate || endDate) {
            onChange({
                startDate: startDate || undefined,
                endDate: endDate || undefined,
            });
        } else {
            onChange({
                startDate: undefined,
                endDate: undefined,
            });
        }
        setIsOpen(false);
    }, [startDate, endDate, onChange]);

    const handleClear = useCallback(() => {
        setStartDate("");
        setEndDate("");
        onChange({
            startDate: undefined,
            endDate: undefined,
        });
    }, [onChange]);

    // Quick date range presets
    const handlePreset = useCallback((days: number) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);

        setStartDate(start.toISOString().split("T")[0]);
        setEndDate(end.toISOString().split("T")[0]);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Update state when filterParams change (e.g., from URL)
    useEffect(() => {
        setStartDate(getInitialStartDate());
        setEndDate(getInitialEndDate());
    }, [getInitialStartDate, getInitialEndDate]);

    const hasFilter = startDate || endDate;

    const formatDateRange = () => {
        if (!hasFilter) return "";
        if (startDate && endDate) {
            return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`;
        }
        if (startDate) {
            return `From ${new Date(startDate).toLocaleDateString()}`;
        }
        return `Until ${new Date(endDate).toLocaleDateString()}`;
    };

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                {hasFilter && (
                    <span className="text-xs text-blue-600 font-normal">
                        ({formatDateRange()})
                    </span>
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute top-full ${position === "left" ? "left-0" : "right-0"} mt-1 bg-white rounded shadow-lg z-50 w-[350px] flex flex-col max-h-[500px]`} onClick={(e) => e.stopPropagation()}>
                    {/* Quick Presets */}
                    <div className="p-3">
                        <label className="text-sm font-medium mb-2 block">Quick Select</label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => handlePreset(7)}
                                className="px-3 py-1 text-sm border border-light-grey rounded hover:bg-light-light-grey"
                            >
                                Last 7 days
                            </button>
                            <button
                                onClick={() => handlePreset(30)}
                                className="px-3 py-1 text-sm border border-light-grey rounded hover:bg-light-light-grey"
                            >
                                Last 30 days
                            </button>
                            <button
                                onClick={() => handlePreset(90)}
                                className="px-3 py-1 text-sm border border-light-grey rounded hover:bg-light-light-grey"
                            >
                                Last 90 days
                            </button>
                        </div>
                    </div>

                    {/* Date Inputs */}
                    <div className="p-3 flex flex-col gap-3">
                        <div>
                            <label className="text-sm font-medium block mb-1">Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full border border-light-grey rounded px-3 py-2"
                                max={endDate || undefined}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium block mb-1">End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full border border-light-grey rounded px-3 py-2"
                                min={startDate || undefined}
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="p-3 flex gap-2">
                        <ButtonV2 label="Clear" color="gray" onClick={handleClear} />
                        <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply} />
                    </div>
                </div>
            )}
        </div>
    );
};
